import { useState } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { TRASH_MAX_PAGE_SIZE, TrashEntityType } from "@ark/shared";
import { useQuery } from "@tanstack/react-query";

import { trashListQueryDocument, TrashListResponse, TrashScope } from "./trash.gql";

interface UseTrashListProps {
  workspaceId?: string;
  scope: TrashScope;
  entityType?: TrashEntityType;
  teamId?: string;
}

interface TrashCursor {
  deletedAt: string;
  id: string;
}

export const useTrashList = ({ workspaceId, scope, entityType, teamId }: UseTrashListProps) => {
  const listKey: string = JSON.stringify([workspaceId, scope, entityType, teamId]);
  const [pagination, setPagination] = useState<{ listKey: string; cursorList: TrashCursor[] }>({
    listKey,
    cursorList: [],
  });
  const cursorList: TrashCursor[] = pagination.listKey === listKey ? pagination.cursorList : [];
  const cursor: TrashCursor | undefined = cursorList.at(-1);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...queryKeys.trash.list(workspaceId ?? "", scope, entityType, teamId), cursor],
    queryFn: async () => {
      const client = createOperationClient("TrashList");
      return await client.request<TrashListResponse>(trashListQueryDocument, {
        input: { workspaceId: workspaceId ?? "", scope, entityType, teamId, cursor },
      });
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });

  const fetchedItemList = data?.trashList ?? [];
  const itemList = fetchedItemList.slice(0, TRASH_MAX_PAGE_SIZE);
  const hasNextPage: boolean = fetchedItemList.length > TRASH_MAX_PAGE_SIZE;
  const nextPage = (): void => {
    const lastItem = itemList.at(-1);
    if (!hasNextPage || !lastItem) return;
    setPagination({ listKey, cursorList: [...cursorList, { id: lastItem.id, deletedAt: lastItem.deletedAt }] });
  };
  const previousPage = (): void => setPagination({ listKey, cursorList: cursorList.slice(0, -1) });

  return {
    itemList,
    hasNextPage,
    hasPreviousPage: cursorList.length > 0,
    nextPage,
    previousPage,
    loading: isLoading,
    error,
    refetch,
  };
};
